import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import {
  Async, Avatar, Badge, Button, Card, Cell, Empty, Field, Modal, Note, PageHead, Table,
} from '@/components/ui';
import { InviteButton } from '@/components/common/InviteButton';

interface Doctor {
  id: string; full_name: string; email: string | null; phone: string | null;
  units: string | null; party_id: string | null;
  /** Ya entró y eligió su contraseña. **No** es «tiene credencial»: una
   *  contraseña temporal deja fila sin que la persona haya entrado nunca, y
   *  contar filas marcaba como activa a gente que todavía hay que invitar. */
  account_active: number;
  billing_ready: boolean; profile_count: number; is_active: number;
  /** `CLIENT_ADMIN` es el administrador del consultorio, no un doctor. Se
   *  lista igual y se marca: en varios consultorios es él quien tiene la
   *  identidad fiscal, y sin verlo acá esta pantalla miente sobre quién puede
   *  facturar. */
  app_role: string;
  /** La foto ya se subía desde el perfil del doctor y el listado ya la
   *  devolvía (el endpoint hace `select p.*`): lo único que faltaba era
   *  declararla acá y mostrarla. */
  photo_key: string | null;
}

/**
 * Lista o galería.
 *
 * La galería no es nueva: era la vista de móvil, forzada por breakpoint. Acá
 * pasa a ser elegible también en escritorio y es la que abre por defecto,
 * porque a un directorio de personas se entra buscando una cara y un
 * consultorio, no comparando seis columnas.
 *
 * Es el mismo `.segmented` que la densidad de la galería de consultorios, y se
 * guarda igual, en `localStorage`. En el admin no hay un tercer patrón de
 * conmutador y no conviene inventarlo.
 */
type DoctorView = 'gallery' | 'list';
const VIEW_KEY = 'cleanblue:doctors-view';

const VIEW_OPTIONS: { key: DoctorView; label: string }[] = [
  { key: 'gallery', label: 'Galería' },
  { key: 'list', label: 'Lista' },
];

/**
 * El directorio de doctores.
 *
 * La columna dice **Facturación**, no «Cédula». Lo que hay que saber de un
 * vistazo no es si hay un número guardado: es si a esa persona se le puede
 * emitir factura electrónica. Sin razón social o sin correo tampoco se puede, y
 * una columna que solo mira la cédula da un falso «listo».
 */
export function DoctorsPage({ embedded }: { embedded?: boolean } = {}) {
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [billing, setBilling] = useState<'' | 'ready' | 'incomplete'>('');
  /* Por consultorio, no por nombre. Un directorio de 84 médicos se recorre
     buscando «quién está en el 512», y el nombre alfabético no ayuda a eso.
     Los que no tienen consultorio quedan al final: lo resuelve el `order by`
     del worker, porque `units` es un `group_concat` NULL y SQLite pone los
     NULL primero — abrir la lista con los huérfanos arriba se leería como si
     el listado estuviera roto. */
  const [sort, setSort] = useState<'name' | 'unit'>('unit');
  const [view, setView] = useState<DoctorView>(
    () => (localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'gallery'),
  );

  function changeView(next: DoctorView) {
    setView(next);
    localStorage.setItem(VIEW_KEY, next);
  }

  const params = new URLSearchParams({ role: 'CLIENT_MEMBER,CLIENT_ADMIN', sort });
  if (search.trim()) params.set('search', search.trim());
  if (billing) params.set('billing', billing);

  const query = useApi<{ profiles: Doctor[] }>(`/admin/profiles?${params}`);

  const newDoctorAction = (
    <Button variant="primary" onClick={() => setCreating(true)}>Nuevo doctor</Button>
  );

  return (
    <>
      {embedded
        ? <div className="row mb-2"><span className="spacer" />{newDoctorAction}</div>
        : (
          <PageHead
            title="Doctores"
            subtitle="Las personas asociadas a los consultorios. Un doctor puede pertenecer a más de uno, y los administradores aparecen marcados."
            actions={newDoctorAction}
          />
        )}

      <div className="row row--wrap mb-2">
        <input
          className="input"
          placeholder="Buscar por nombre, correo o teléfono…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />
        <select className="select" value={billing} style={{ width: 190 }}
          onChange={(e) => setBilling(e.target.value as typeof billing)}>
          <option value="">Toda la facturación</option>
          <option value="incomplete">Facturación incompleta</option>
          <option value="ready">Facturación completa</option>
        </select>
        <select className="select" value={sort} style={{ width: 205 }}
          onChange={(e) => setSort(e.target.value as typeof sort)}>
          <option value="unit">Ordenar por consultorio</option>
          <option value="name">Ordenar por nombre</option>
        </select>

        <span className="spacer" />

        {/* El conmutador solo manda de 760px para arriba: debajo de eso la
            tabla no cabe y la galería es lo único que hay, así que ofrecer la
            elección ahí sería ofrecer un botón que no hace nada. */}
        <div
          className="segmented segmented--view"
          role="group"
          aria-label="Cómo ver el directorio"
          style={{ '--seg-n': VIEW_OPTIONS.length, '--seg-i': VIEW_OPTIONS.findIndex((v) => v.key === view) } as React.CSSProperties}
        >
          <div className="segmented__pill" />
          {VIEW_OPTIONS.map((v) => (
            <button
              key={v.key}
              type="button"
              aria-pressed={view === v.key}
              className={`segmented__opt${view === v.key ? ' is-active' : ''}`}
              onClick={() => { if (view !== v.key) haptic.light(); changeView(v.key); }} // HAPTIC_FEEDBACK
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <Async query={query} empty={(d) => d.profiles.length === 0 && (
        <Empty
          icon="doctor"
          title={search || billing ? 'Nadie coincide con ese filtro' : 'Todavía no hay doctores cargados'}
        />
      )}>
        {(data) => {
          const missing = data.profiles.filter((p) => !p.billing_ready).length;
          return (
            <>
              {missing > 0 && !billing && (
                <Note tone="warn">
                  A <strong>{missing}</strong> de {data.profiles.length} les falta algo para poder
                  facturarles. Se les puede cobrar igual; lo que no sale es la factura electrónica.
                </Note>
              )}

              {/* Dos vistas, una sola fuente: los filtros (búsqueda,
                  facturación, orden) y el aviso de arriba aplican a las dos,
                  porque la que consulta es la misma query.

                  Cuando la elección es «lista», la tabla se muestra de 760px
                  para arriba y la galería queda como la vista de teléfono —
                  exactamente el comportamiento que ya existía. Cuando es
                  «galería», se renderiza una sola grilla sin clase de
                  breakpoint, que sirve a los dos tamaños. Así no se monta el
                  DOM de 84 filas y 84 cards al mismo tiempo salvo en el caso
                  que ya lo hacía. */}
              {view === 'list' ? (
                <>
                  <div className="doctors-desktop">
                    <Card flush>
                      <Table head={
                        <tr>
                          <th>Nombre</th><th>Consultorios</th><th>Contacto</th>
                          <th>Facturación</th><th>Cuenta</th><th />
                        </tr>
                      }>
                        {data.profiles.map((p) => (
                          <tr key={p.id}>
                            <td className="strong">
                              <Link to={`/admin/doctores/${p.id}`}>{p.full_name}</Link>
                              {p.app_role === 'CLIENT_ADMIN' && (
                                <> <Badge tone="info" plain title="Administra el consultorio; puede no ser doctor">Administrador</Badge></>
                              )}
                            </td>
                            <td className="muted">{p.units ?? '—'}</td>
                            <td className="small muted">
                              <Cell sub={p.phone}>{p.email ?? '—'}</Cell>
                            </td>
                            <td>
                              {p.billing_ready
                                ? <Badge tone="ok">Completa</Badge>
                                : <Badge tone="warn">Incompleta</Badge>}
                            </td>
                            <td>
                              {p.account_active
                                ? <Badge tone="ok" plain>Activa</Badge>
                                : <span className="muted small">Sin cuenta</span>}
                            </td>
                            <td className="right">
                              {!p.account_active && p.email && (
                                <InviteButton
                                  profileId={p.id}
                                  personName={p.full_name}
                                  email={p.email}
                                />
                              )}
                            </td>
                          </tr>
                        ))}
                      </Table>
                    </Card>
                  </div>

                  <div className="doctors-mobile person-grid">
                    {data.profiles.map((p) => (
                      <DoctorCard key={p.id} doctor={p} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="person-grid">
                  {data.profiles.map((p) => (
                    <DoctorCard key={p.id} doctor={p} />
                  ))}
                </div>
              )}
            </>
          );
        }}
      </Async>

      {creating && <NewDoctorModal onClose={() => setCreating(false)} />}
    </>
  );
}

/**
 * La tarjeta de la galería.
 *
 * **Un doctor, una tarjeta**, aunque esté en varios consultorios: `units` llega
 * como `"512, 618"` y se muestran todos los códigos juntos. La alternativa
 * —una tarjeta por consultorio— haría que buscar a una persona la encontrara
 * dos veces y que el contador «84 doctores» dejara de cuadrar con lo que se ve.
 * (Supuesto pendiente de confirmación del dueño; si contesta distinto, lo que
 * cambia es esta función y nada más.)
 *
 * La foto sale del mismo endpoint privado que ya usa el perfil, con el `?v=`
 * de cache-busting. La mayoría de los 84 no tiene, y ahí `Avatar` cae a las
 * iniciales sobre `--blue-100`: es información (quién es) en vez del mismo
 * ícono gris repetido 84 veces. Va `lazy` y con dimensiones fijas.
 */
function DoctorCard({ doctor: p }: { doctor: Doctor }) {
  return (
    <div className="person-card">
      <Avatar
        src={p.photo_key ? `/api/admin/profiles/${p.id}/photo?v=${p.photo_key}` : null}
        name={p.full_name}
        size={56}
        lazy
      />
      {/* El enlace se estira sobre toda la card con un `::after`, así que la
          tarjeta entera es clicable sin meter un `<button>` dentro de un `<a>`,
          que no es HTML válido. */}
      <Link to={`/admin/doctores/${p.id}`} className="person-card__name">{p.full_name}</Link>

      {/* «Consultorio 501», no «501» suelto: en la tabla el encabezado de la
          columna decía qué era ese número, y en la galería no hay encabezado.
          El plural sale de si `units` trae más de un código. */}
      <div className={`person-card__where${p.units ? '' : ' person-card__where--none'}`}>
        {p.units
          ? `${p.units.includes(',') ? 'Consultorios' : 'Consultorio'} ${p.units}`
          : 'Sin consultorio'}
      </div>

      {(p.email || p.phone) && (
        <div className="person-card__contact">{p.email ?? p.phone}</div>
      )}

      {/* Facturación y cuenta no se pierden al cambiar de vista: son las dos
          cosas que el admin viene a mirar en este directorio. */}
      <div className="person-card__meta">
        {/* Primero qué ES la persona, después sus estados: sin la marca, un
            administrador se lee como un doctor más y su «Incompleta» se
            interpreta mal. Los doctores no llevan insignia — son el caso
            normal, y 84 marcas iguales no distinguirían nada. */}
        {p.app_role === 'CLIENT_ADMIN' && (
          <Badge tone="info" plain title="Administra el consultorio; puede no ser doctor">Administrador</Badge>
        )}
        {p.billing_ready
          ? <Badge tone="ok">Completa</Badge>
          : <Badge tone="warn">Incompleta</Badge>}
        {p.account_active
          ? <Badge tone="ok" plain>Activa</Badge>
          : <Badge tone="neutral" plain>Sin cuenta</Badge>}
      </div>

      {/* «Invitar» es el único control de la tarjeta y solo aparece cuando de
          verdad se puede invitar (sin cuenta y con correo). Hoy eso es casi
          todo el directorio —el portal está construido y nadie fue invitado
          todavía—, así que va en `ghost`: sin caja ni borde. Con el botón
          normal, 81 rectángulos idénticos convertían la galería en una barra
          de acciones y tapaban lo que la card viene a mostrar, que es gente. */}
      {!p.account_active && p.email && (
        <div className="person-card__action">
          <InviteButton profileId={p.id} personName={p.full_name} email={p.email} ghost />
        </div>
      )}
    </div>
  );
}


function NewDoctorModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '' });
  const mutation = useApiMutation(
    () => api.post('/admin/profiles', {
      full_name: form.full_name,
      email: form.email || undefined,
      phone: form.phone || undefined,
      app_role: 'CLIENT_MEMBER',
      preferred_channel: 'WHATSAPP',
    }),
    ['/admin/profiles'],
    { onSuccess: () => { haptic.success(); onClose(); } }, // HAPTIC_FEEDBACK
  );

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal
      title="Nuevo doctor"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!form.full_name || mutation.isPending}
            onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Guardando…' : 'Crear'}
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      <Note tone="info">
        Al crearlo también se crea su registro como pagador, para que pueda entrar en un reparto.
      </Note>

      <div className="mt-2">
        <Field label="Nombre completo" required>
          <input className="input" value={form.full_name} onChange={set('full_name')} autoFocus />
        </Field>
        <Field label="Correo" hint="Es su usuario para entrar al portal.">
          <input className="input" type="email" value={form.email} onChange={set('email')} />
        </Field>
        <Field label="Teléfono" hint="WhatsApp, si es distinto del teléfono del consultorio.">
          <input className="input" value={form.phone} onChange={set('phone')} />
        </Field>
      </div>
    </Modal>
  );
}
